import { CtaApi } from '@myre/app-agent-generated/sdk/angular/CtaApi';
import { type UiLibI18n } from '@myre/domain-i18n-wordings';

import { inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { WORDINGS } from '../../myre-wordings-loader';

import { type CtaItem } from '../interfaces/chat.interfaces';

import { ChatContextService } from './chat-context.service';

export interface CtaResult {
  success: boolean;
  message: string;
}

@Injectable({ providedIn: 'root' })
export class CtaService {
  private readonly _ctaApi = inject(CtaApi);
  private readonly _chatContextService = inject(ChatContextService);
  private readonly _wordings = inject<UiLibI18n>(WORDINGS);

  private get _chatWordings() {
    return this._wordings._library.chat;
  }

  public readonly results = signal<Map<string, CtaResult>>(new Map());

  public async execute(cta: CtaItem): Promise<void> {
    try {
      const customerId = this._chatContextService.context().params.customerId;

      const result = await firstValueFrom(
        this._ctaApi.execute(
          { customerId },
          {
            action: cta.actionType,
            payload: cta.payload,
          },
        ),
      );

      this._setResult(cta.label, {
        success: result.success,
        message: result.message ?? this._chatWordings.actionCompleted(),
      });
    } catch (error) {
      this._setResult(cta.label, {
        success: false,
        message: `${this._chatWordings.actionFailed()}: ${(error as Error).message}`,
      });
    }
  }

  private _setResult(label: string, result: CtaResult): void {
    this.results.update(map => {
      const updated = new Map(map);

      updated.set(label, result);

      return updated;
    });
  }
}
